<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { IconRadio, SelectInput } from '@/shared/ui';
  import { Label } from '@/shared/ui/label';
  import { SearchCombobox } from '@/shared/ui/search-combobox';
  import { useApprovalConfigOptions } from '../../composables/useApprovalConfigOptions';
  import {
    FUNCTIONAL_INSPECTION_TYPE_OPTIONS,
    requiresFunctionalInspectionType,
    type ApprovalConfigValues,
    type FunctionalInspectionType,
  } from '../../lib/approval-submission';

  defineProps<{
    disabled?: boolean;
    /** Revision 2+: the module is fixed to the one of the previous revisions. */
    moduleLocked?: boolean;
    lockedModuleName?: string;
    isLoadingLockedModule?: boolean;
  }>();

  const model = defineModel<ApprovalConfigValues>({ required: true });

  const {
    moduleSearchTerm,
    filteredModuleOptions,
    recordOriginOptions,
    recordTypeOptions,
    isLoadingModules,
    isLoadingRecordTypes,
    loadInitialOptions,
    loadRecordTypes,
    findRecordTypeName,
  } = useApprovalConfigOptions();

  onMounted(loadInitialOptions);

  const moduleValue = computed(() =>
    model.value.moduleId === '' ? '' : String(model.value.moduleId)
  );
  const recordTypeValue = computed(() =>
    model.value.recordTypeId === '' ? '' : String(model.value.recordTypeId)
  );
  const showsInspectionType = computed(() =>
    requiresFunctionalInspectionType(model.value.recordTypeName)
  );

  function patch(changes: Partial<ApprovalConfigValues>) {
    model.value = { ...model.value, ...changes };
  }

  function toId(value: string | number | undefined): number | '' {
    return value === '' || value == null ? '' : Number(value);
  }

  function handleModuleChange(value: string) {
    patch({ moduleId: toId(value) });
  }

  // The parent never writes origin or type from outside (there is no prefill), so the dependent
  // resets live in the handlers rather than in watchers on the model.
  function handleRecordOriginChange(value: string | number) {
    const recordOriginId = toId(value);
    if (recordOriginId === model.value.recordOriginId) return;

    patch({ recordOriginId, recordTypeId: '', recordTypeName: '', functionalInspectionType: '' });
    void loadRecordTypes(recordOriginId);
  }

  function handleRecordTypeChange(value: string | undefined) {
    const recordTypeId = toId(value);
    const recordTypeName = findRecordTypeName(recordTypeId);

    patch({
      recordTypeId,
      recordTypeName,
      functionalInspectionType: requiresFunctionalInspectionType(recordTypeName)
        ? model.value.functionalInspectionType
        : '',
    });
  }

  function handleInspectionTypeChange(value: string | undefined) {
    patch({ functionalInspectionType: (value ?? '') as FunctionalInspectionType | '' });
  }
</script>

<template>
  <section class="flex flex-col gap-6" aria-labelledby="approval-config-title">
    <h3 id="approval-config-title" class="text-[15px] font-semibold text-gray-07">Configuração</h3>

    <div class="flex flex-col">
      <Label class="flex h-8 items-center text-sm font-semibold text-gray-07">
        Origem do Registro:
      </Label>
      <SelectInput
        :model-value="model.recordOriginId"
        :options="recordOriginOptions"
        label="Escolha uma origem"
        container-class="w-[268px]"
        button-class="h-11 bg-white-01 border border-gray-02 whitespace-nowrap"
        :disabled="disabled"
        data-testid="approval-record-origin"
        @update:model-value="handleRecordOriginChange"
      />
    </div>

    <div
      v-if="model.recordOriginId !== ''"
      class="flex flex-col"
      data-testid="approval-record-type"
    >
      <Label class="flex h-8 items-center text-sm font-semibold text-gray-07">
        Tipo de Registro:
      </Label>
      <span v-if="isLoadingRecordTypes" class="text-sm text-gray-03">Carregando tipos...</span>
      <span v-else-if="recordTypeOptions.length === 0" class="text-sm text-gray-03"
        >Nenhum tipo encontrado para esta origem</span
      >
      <div v-else class="flex flex-wrap gap-8">
        <IconRadio
          v-for="type in recordTypeOptions"
          :key="type.id"
          :model-value="recordTypeValue"
          variant="medium"
          :value="String(type.id)"
          :label="type.name"
          name="approvalRecordType"
          :disabled="disabled"
          @update:model-value="handleRecordTypeChange"
        />
      </div>
    </div>

    <div
      v-if="showsInspectionType"
      class="flex flex-col"
      data-testid="approval-functional-inspection-type"
    >
      <Label class="flex h-8 items-center text-sm font-semibold text-gray-07">
        Tipo de Inspeção Funcional:
      </Label>
      <div class="flex flex-wrap gap-8">
        <IconRadio
          v-for="option in FUNCTIONAL_INSPECTION_TYPE_OPTIONS"
          :key="option.value"
          :model-value="model.functionalInspectionType"
          variant="medium"
          :value="option.value"
          :label="option.label"
          name="approvalFunctionalInspectionType"
          :disabled="disabled"
          @update:model-value="handleInspectionTypeChange"
        />
      </div>
    </div>

    <div class="flex flex-col">
      <Label class="flex h-8 items-center text-sm font-semibold text-gray-07">Módulo:</Label>
      <SearchCombobox
        :model-value="moduleValue"
        :options="filteredModuleOptions"
        placeholder="Buscar"
        search-placeholder="Buscar"
        empty-message="Nenhum módulo encontrado."
        trigger-icon="search"
        class="h-11 w-[335px] rounded-lg border-gray-02 bg-white-01"
        :initial-label="lockedModuleName"
        :loading="isLoadingModules || isLoadingLockedModule"
        :disabled="disabled || moduleLocked"
        @update:model-value="handleModuleChange"
        @search="moduleSearchTerm = $event"
      />
      <span
        v-if="moduleLocked"
        class="mt-1.5 text-sm text-gray-03"
        data-testid="approval-module-locked-hint"
        >As revisões anteriores deste registro estão neste módulo.</span
      >
    </div>
  </section>
</template>
